import formatTZ from 'date-fns-tz/format';
import utcToZonedTime from 'date-fns-tz/utcToZonedTime';
import { formatDateCustom } from './format-date-custom.js';
import isSameYear from 'date-fns/isSameYear';
import { formatTime } from './format-time.js';
import differenceInSeconds from 'date-fns/differenceInSeconds';
import subDays from 'date-fns/subDays';
import addDays from 'date-fns/addDays';
import isSameDay from 'date-fns/isSameDay';
import isSameWeek from 'date-fns/isSameWeek';
import formatDistanceStrict from 'date-fns/formatDistanceStrict';

/**
 * Formats the difference between `date` and `now` in hours and minutes.
 *
 * If difference is less than one minute, it will return "0 minutes".
 *
 * - "1 hour and 30 minutes"
 * - "1 hour"
 * - "30 minutes"
 * - "0 minutes"
 */
function differenceInHoursAndMinutes(date, now, includeAffix) {
    const secondsDiff = differenceInSeconds(date, now);
    const relativeTime = secondsDiff < 0 ? 'past' : 'future';
    const secondsAbs = Math.abs(secondsDiff);
    const hours = Math.floor(secondsAbs / 3600);
    const minutes = Math.floor((secondsAbs % 3600) / 60);
    const hoursString = hours === 1 ? `${hours} hour` : hours > 1 ? `${hours} hours` : '';
    const minutesString = minutes === 1 ? `${minutes} minute` : `${minutes} minutes`;
    const formatedTime = hours > 0 && minutes > 0 ? `${hoursString} and ${minutesString}` : hoursString || minutesString;
    if (includeAffix) {
        return relativeTime === 'past' ? `${formatedTime} ago` : `in ${formatedTime}`;
    }
    else {
        return formatedTime;
    }
}
/**
 * Format a date relative to now, with decreasing specificity the further in the past or future the date is.
 *
 * Use this for UIs where a general timeline is important, but precise specificity is not (e.g. when a field
 * was edited, a message was sent, or comment was written). As a best practice, show the absolute time on
 * hover of the UI element (the HTML `title` attribute and the `time` element are your friends).
 *
 * Defaults:
 * - `format` = "long"
 * - `specificity` = "specific"
 * - `includeAffixForDistanceFormats` = "false"
 * - `includePrefixForAbsoluteFormats` = "false"
 * - `sentenceCaseRelativeTime` = "true"
 */
function formatRelative(startDate, options) {
    const { format = 'long', specificity = 'specific', includeAffixForDistanceFormats = false, includePrefixForAbsoluteFormats = false, sentenceCaseRelativeTime = true, ianaTimeZone, } = options || {};
    let now = new Date();
    let convertedDate = new Date(startDate);
    // if setting the time zone, convert the time to the zoned time
    if (ianaTimeZone) {
        now = utcToZonedTime(now, ianaTimeZone);
        convertedDate = utcToZonedTime(convertedDate, ianaTimeZone);
    }
    const prefix = includePrefixForAbsoluteFormats ? 'on ' : '';
    const secondsDiff = differenceInSeconds(convertedDate, now);
    const diffIsLessThanOneMinute = Math.abs(secondsDiff) < 60;
    const isYesterday = isSameDay(convertedDate, subDays(now, 1));
    const isTomorrow = isSameDay(convertedDate, addDays(now, 1));
    const timeZoneString = ianaTimeZone
        ? formatTZ(convertedDate, ' zzz', { timeZone: ianaTimeZone })
        : '';
    const timeString = formatTime(convertedDate, ianaTimeZone);
    const sharedFormatOptions = { format, ianaTimeZone };
    const monthDayTimeString = formatDateCustom(convertedDate, Object.assign({ includeDayOfWeek: false, includeMonthAndDay: true, includeYear: false, includeTime: true }, sharedFormatOptions));
    const distanceString = formatDistanceStrict(convertedDate, now, {
        addSuffix: includeAffixForDistanceFormats,
    });
    if (diffIsLessThanOneMinute) {
        const relativeTime = secondsDiff < 0 ? 'past' : 'future';
        if (sentenceCaseRelativeTime) {
            return relativeTime === 'past' ? 'Just now' : 'Now';
        }
        else {
            return relativeTime === 'past' ? 'just now' : 'now';
        }
    }
    else if (isSameDay(convertedDate, now)) {
        const todayString = sentenceCaseRelativeTime ? 'Today' : 'today';
        switch (specificity) {
            case 'more-general':
                return differenceInHoursAndMinutes(convertedDate, now, includeAffixForDistanceFormats);
            case 'general':
                return `${todayString}${timeZoneString}`;
            case 'specific':
                return `${todayString}, ${timeString}`;
            case 'more-specific':
                return `${todayString}, ${monthDayTimeString}`;
        }
    }
    else if (isYesterday || isTomorrow) {
        const yesterdayString = sentenceCaseRelativeTime ? 'Yesterday' : 'yesterday';
        const tomorrowString = sentenceCaseRelativeTime ? 'Tomorrow' : 'tomorrow';
        const dayString = isYesterday ? yesterdayString : tomorrowString;
        switch (specificity) {
            case 'more-general':
                return distanceString;
            case 'general':
                return `${dayString}${timeZoneString}`;
            case 'specific':
                return `${dayString}, ${timeString}`;
            case 'more-specific':
                return `${dayString}, ${monthDayTimeString}`;
        }
    }
    else if (isSameWeek(convertedDate, now)) {
        switch (specificity) {
            case 'more-general':
                return distanceString;
            case 'general':
                return `${prefix}${formatDateCustom(convertedDate, {
                    includeDayOfWeek: true,
                    includeMonthAndDay: false,
                    includeYear: false,
                    includeTime: false,
                    format: 'extra-long',
                    ianaTimeZone,
                })}`;
            case 'specific':
                return `${prefix}${formatDateCustom(convertedDate, {
                    includeDayOfWeek: true,
                    includeMonthAndDay: false,
                    includeYear: false,
                    includeTime: true,
                    format: 'extra-long',
                    ianaTimeZone,
                })}`;
            case 'more-specific':
                return `${prefix}${formatDateCustom(convertedDate, Object.assign({ includeDayOfWeek: true, includeMonthAndDay: true, includeYear: false, includeTime: true }, sharedFormatOptions))}`;
        }
    }
    else if (isSameYear(convertedDate, now)) {
        switch (specificity) {
            case 'more-general':
            case 'general':
                return `${prefix}${formatDateCustom(convertedDate, Object.assign({ includeDayOfWeek: false, includeMonthAndDay: true, includeYear: false, includeTime: false }, sharedFormatOptions))}`;
            case 'specific':
                return `${prefix}${monthDayTimeString}`;
            case 'more-specific':
                return `${prefix}${formatDateCustom(convertedDate, Object.assign({ includeDayOfWeek: true, includeMonthAndDay: true, includeYear: false, includeTime: true }, sharedFormatOptions))}`;
        }
    }
    else {
        switch (specificity) {
            case 'more-general':
            case 'general':
                return `${prefix}${formatDateCustom(convertedDate, Object.assign({ includeDayOfWeek: false, includeMonthAndDay: true, includeYear: true, includeTime: false }, sharedFormatOptions))}`;
            case 'specific':
                return `${prefix}${formatDateCustom(convertedDate, Object.assign({ includeDayOfWeek: false, includeMonthAndDay: true, includeYear: true, includeTime: true }, sharedFormatOptions))}`;
            case 'more-specific':
                return `${prefix}${formatDateCustom(convertedDate, Object.assign({ includeDayOfWeek: true, includeMonthAndDay: true, includeYear: true, includeTime: true }, sharedFormatOptions))}`;
        }
    }
}

export { formatRelative };
//# sourceMappingURL=format-relative.js.map
